import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User, Camera, Loader2, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface ClientAvatarProps {
  userId: string;
  name?: string | null;
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  editable?: boolean;
}

const sizeClasses = {
  sm: "w-7 h-7 text-[10px]",
  md: "w-9 h-9 text-xs",
  lg: "w-12 h-12 text-sm",
  xl: "w-20 h-20 text-xl",
};

const iconSizes = { sm: 12, md: 14, lg: 18, xl: 28 };

// Simple in-memory cache to avoid redundant queries
const photoCache: Record<string, string | null> = {};

const ClientAvatar = ({ userId, name, size = "md", className, editable = true }: ClientAvatarProps) => {
  const [photoUrl, setPhotoUrl] = useState<string | null>(photoCache[userId] ?? null);
  const [loaded, setLoaded] = useState(false);
  const [queried, setQueried] = useState(userId in photoCache);
  const [expanded, setExpanded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!userId || queried) return;

    const fetchFacePhoto = async () => {
      const { data } = await supabase
        .from("client_photos")
        .select("file_path")
        .eq("user_id", userId)
        .eq("category", "face")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data?.file_path) {
        const { data: signedData } = await supabase.storage
          .from("client-photos")
          .createSignedUrl(data.file_path, 3600);
        const url = signedData?.signedUrl || null;
        photoCache[userId] = url;
        setPhotoUrl(url);
      } else {
        photoCache[userId] = null;
      }
      setQueried(true);
    };

    fetchFacePhoto();
  }, [userId, queried]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const filePath = `${userId}/face/${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage.from("client-photos").upload(filePath, file);
      if (uploadError) throw uploadError;
      const { error: insertError } = await supabase.from("client_photos").insert({
        user_id: userId, category: "face", file_path: filePath, uploaded_by: "admin",
      });
      if (insertError) throw insertError;

      const { data: signedData } = await supabase.storage.from("client-photos").createSignedUrl(filePath, 3600);
      const newUrl = signedData?.signedUrl || null;
      photoCache[userId] = newUrl;
      setPhotoUrl(newUrl);
      setLoaded(false);
      toast.success("Face photo updated!");
    } catch (err: any) {
      toast.error(err.message || "Upload failed");
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDelete = async () => {
    try {
      const { data } = await supabase
        .from("client_photos")
        .select("id, file_path")
        .eq("user_id", userId)
        .eq("category", "face")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data) {
        await supabase.storage.from("client-photos").remove([data.file_path]);
        await supabase.from("client_photos").delete().eq("id", data.id);
        photoCache[userId] = null;
        setPhotoUrl(null);
        setExpanded(false);
        toast.success("Face photo removed");
      }
    } catch {
      toast.error("Failed to delete photo");
    }
  };

  const initial = (name || "?").charAt(0).toUpperCase();

  return (
    <>
      <div
        className={cn(
          "rounded-full bg-primary/10 flex items-center justify-center overflow-hidden shrink-0 ring-1 ring-border/30 cursor-pointer",
          sizeClasses[size],
          className
        )}
        onClick={() => setExpanded(true)}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={name || "Client"}
            className={cn(
              "w-full h-full object-cover transition-opacity duration-200",
              loaded ? "opacity-100" : "opacity-0"
            )}
            onLoad={() => setLoaded(true)}
            onError={() => { setPhotoUrl(null); photoCache[userId] = null; }}
          />
        ) : (
          <span className="font-bold text-primary">{initial}</span>
        )}
      </div>

      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="max-w-sm p-4 bg-card border-border/50">
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={name || "Client"}
              className="w-full h-auto rounded-lg object-contain max-h-[70vh]"
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <User size={48} className="mb-3 opacity-30" />
              <p className="text-sm">No face photo yet</p>
            </div>
          )}
          {name && <p className="text-center text-sm font-medium mt-1">{name}</p>}

          {editable && (
            <div className="flex gap-2 mt-2 justify-center">
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                onChange={handleUpload}
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="text-xs gap-1.5"
              >
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
                {photoUrl ? "Change Photo" : "Upload Photo"}
              </Button>
              {photoUrl && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDelete}
                  className="text-xs gap-1.5 text-destructive hover:bg-destructive/10"
                >
                  <Trash2 size={14} /> Remove
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClientAvatar;
